import { Injectable } from '@angular/core';
import authHeader from '../helpers/auth-header';
import { HttpClient } from '@angular/common/http';
import { UserProfile } from '../interfaces/UserProfile';
import { Address } from '../interfaces/address';
import { Customer } from '../interfaces/customer';
import { PaymentMethodCc } from '../interfaces/payment-method-cc';
import { PaymentMethods } from '../interfaces/payment-methods';
import { environment } from 'src/environments/environment';
import { catchError, map } from 'rxjs';
import { AuthenticationService } from './authentication.service';

@Injectable({
     providedIn: 'root'
})
export class PaymentsService {
     walletURL: string = '';
     billingState: string = 'get';
     userProfile?: UserProfile;
     customer?: Customer;
     methodsArray?: PaymentMethods[];
     previousMethodsArray?: PaymentMethods[];
     numbersRegex: RegExp = /^([0-9]*)$/;
     constructor(private http: HttpClient,
          private authenticationService: AuthenticationService) { }

     getBillingAccounts(): any {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };

          const url = `${environment.APP_API_URL}/api/v1/BillingAccount/GetBillingAccounts`;
          return this.http.get(url, config)
               .pipe(
                    map((response) => {
                         if (response) {
                              this.methodsArray = response as PaymentMethods[];
                              return response;
                         }
                         return null;
                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    }
               )
          )
     }

     createCDBBillingAccount(lang: string = 'en') {
          const userProfile = this.getUserProfile() as UserProfile;
          const customer = userProfile?.customer as Customer;
          const address = customer?.address as Address;
          const CdbUrl = environment.APP_PSP_CDB_URL;
          const createInfo = {
               CdbUrl: CdbUrl,
               Token: this.uuidv4(),
               UserHostName: "",
               CustomerId: customer?.customerId,
               CustomerEmail: customer?.cdbPrimaryContactEmail,
               NotificationCallbackUrl: '---',
               Options: {
                    CcEnabled: true,
                    AchEnabled: (customer?.address?.country === 'USA' ? true : false),
                    DisplayCancelButton: true,
                    Localization: lang.toLowerCase(),
                    ForeignAddressesEnabled: true
               },
               Address: {}
          }
          createInfo.Address = {
               Address1: address.addressLine1,
               Address2: address.addressLine2,
               City: address.city,
               State: address.state,
               PostalCode: address.zip,
               Country: address.country
          }

          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               },
          };


          const url = environment.APP_API_URL + '/api/v1/Wallet/GetIframeURL';
          return this.http.post(url, createInfo, { headers: config.headers, responseType: 'text' })
               .pipe(
                    map((response) => {
                         return response as string;
                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }

     editCDBCreditCard(account: PaymentMethodCc, lang: string = 'EN',) {
          const userProfile = this.getUserProfile() as UserProfile;
          const customer = userProfile?.customer as Customer;
          let isInUSA = true;
          if (account.address && account.address.country && account.address.postalCode) {
               isInUSA = this.isUSA(account.address.country, account.address.postalCode);
          }
          const CdbUrl = environment.APP_PSP_CDB_URL;
          const editInfo = {
               CdbUrl: CdbUrl,
               Token: this.uuidv4(),
               UserHostName: '',
               CustomerId: customer?.customerId,
               CustomerEmail: customer?.cdbPrimaryContactEmail,
               BillingAccountId: account.id,
               Options: {
                    CcEnabled: true, AchEnabled: (isInUSA ? true : false)
               },
               DisplayCancelButton: true,
               Localization: lang.toLowerCase(),
               ForeignAddressesEnabled: true,
               Address: {}
          }

          if (account.address) {
               editInfo.Address = {
                    Address1: account.address.street1,
                    Address2: account.address.street2,
                    City: account.address.city,
                    // TODO: The state should match the state that came from CDB. Change setting the state once CDB fixes this.
                    State: customer.address!.state,
                    PostalCode: account.address.postalCode,
                    Country: account.address.country
               }
          }

          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          const url = environment.APP_API_URL + '/api/v1/Wallet/GetIframeURL';
          return this.http.post(url, editInfo, { headers: config.headers, responseType: 'text' })
               .pipe(
                    map((response) => (response as string)),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               )
     }

     editCDBACH(accountID: string, lang: string = 'en',) {
          const userProfile = this.getUserProfile() as UserProfile;
          const customer = userProfile?.customer as Customer;
          const customerAddress = customer?.address as Address;
          const isInUSA = this.isUSA(customerAddress.country, customerAddress.zip);
          const CdbUrl = environment.APP_PSP_CDB_URL;
          const editInfo = {
               CdbUrl: CdbUrl,
               Token: this.uuidv4(),
               UserHostName: "",
               CustomerId: customer?.customerId,
               CustomerEmail: customer?.cdbPrimaryContactEmail,
               BillingAccountId: accountID,
               Options: {
                    CcEnabled: true, AchEnabled: (!isInUSA ? true : false), Localization: lang.toLowerCase(),
                    ForeignAddressesEnabled: true
               },
               DisplayCancelButton: true
               ,
               Address: {}
          }
          editInfo.Address = {
               Address1: customerAddress.addressLine1,
               Address2: customerAddress.addressLine2,
               City: customerAddress.city,
               State: customerAddress.state,
               PostalCode: customerAddress.zip,
               Country: customerAddress.country
          }

          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          const url = environment.APP_API_URL + '/api/v1/Wallet/GetIframeURL';
          return this.http.post(url, editInfo, config)
               .pipe(
                    map((response) => (response as any)),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               )
     }

     removeCreditCard(billingAccountId: number): any {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };

          const url = `${environment.APP_API_URL}/api/v1/BillingAccount/DeleteCreditCardAccount/${billingAccountId}`;
          return this.http.delete(url, config)
               .pipe(
                    map((response) => (response as any)),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               )
     }

     setDefaultPaymentMethod(billingAccountId: number, accountType: string, isNewAccount: boolean): any {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };

          const url = `${environment.APP_API_URL}/api/v1/BillingAccount/${billingAccountId}/SetBillingDefaultAccountForCustomer?accountType=${accountType}&isNewAccount=${isNewAccount.toString()}`;
          return this.http.put(url, {}, config)
               .pipe(
                    map((response) => (response as any)),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               )
     }

     togglePaymentMethodActiveState(billingAccountId: number): any {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };

          const url = `${environment.APP_API_URL}/api/v1/BillingAccount/SetAccountActiveState/${billingAccountId}`;
          return this.http.put(url, config)
               .pipe(
                    map((response) => (response as any)),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               )
     }

     sendPaymentMethodAddOrUpdateEmail(billingAccountId: number, isUpdate: boolean): any {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };

          const url = `${environment.APP_API_URL}/api/v1/BillingAccount/SendPaymentMethodAddOrUpdateEmail/?billingAccountId=${billingAccountId}&isUpdate=${isUpdate}`;
          return this.http.post(url, config)
               .pipe(
                    map((response) => (response as any)),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               )
     }

     getUserProfile(): any {
          return this.authenticationService.getUserProfile();
     }

     private uuidv4() {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
               const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
               return v.toString(16);
          });
     }

     mockGetBillingAccounts(): PaymentMethods {
          return this.mockPaymentMethods;
     }

     isUSA(country: string, postalCode: string): boolean {
          // Note 1: Countries in the Production PSP database include:
          //  null, US, USA, Canada, CA, and CANADA
          // Note 2: For extra fun, CDB returns Canadian addresses with the country USA and no state, so you must look at the postal code too.
          const countryIsCanada = country.toLocaleLowerCase().startsWith('ca');
          postalCode = postalCode.replaceAll(' ', '').replace('-', '');
          const isNumericOnly = this.numbersRegex.test(postalCode);
          const postalCodeRightLength = postalCode.length === 5 || postalCode.length === 9;
          return countryIsCanada === false && isNumericOnly && postalCodeRightLength;
     }

     mockPaymentMethods: PaymentMethods = {
          bankAccounts: [{
               accountType: 0,
               acctNo: '981456987',
               bankName: 'UMB Bank',
               customerType: 0,
               firstName: 'Norman',
               lastName: "Herzog",
               nameOnAccount: "Norman Herzog",
               orgName: "Jackson Transport Inc",
               routeNo: "101000609",
               id: 987654321,
               isActive: true,
               isDefault: false
          }],
          creditCards: [
               {
                    address: {
                         city: 'Seattle',
                         country: 'USA',
                         postalCode: '98146',
                         street1: '234 West Holbrook Blvd Suite 700',
                         street2: ''
                    },
                    creditCardExpire: '03/22',
                    creditCardNumber: '*1131',
                    creditCardType: 1,
                    firstName: 'Henry',
                    lastName: 'Herzog',
                    nameOnCard: 'Henry K Herzog',
                    orgName: 'Jackson Transport Inc.',
                    id: 65498731,
                    isActive: true,
                    isDefault: true
               },
               {
                    address: {
                         city: 'San Francisco',
                         country: 'USA',
                         postalCode: '08145',
                         street1: '12354 S 136th Street',
                         street2: ''
                    },
                    creditCardExpire: '08/26',
                    creditCardNumber: '*9877',
                    creditCardType: 3,
                    firstName: 'Frank',
                    lastName: 'Herzog',
                    nameOnCard: 'Frank L Herzog',
                    orgName: 'Jackson Transport Inc.',
                    id: 731654987,
                    isActive: true,
                    isDefault: false
               }
          ]
     }
}
